import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { AtSign } from "lucide-react";

const MentionSelector = ({ 
  value, 
  onChange, 
  placeholder = "Add a note...", 
  rows = 3,
  className = ""
}) => {
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);
  const textareaRef = useRef(null);
  const mentionListRef = useRef(null);
  const token = localStorage.getItem("token");

  // Fetch mentionable users on mount
  useEffect(() => {
    fetchMentionableUsers();
  }, []);

  // Close mention list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mentionListRef.current && !mentionListRef.current.contains(event.target) &&
          textareaRef.current && !textareaRef.current.contains(event.target)) {
        setShowMentionList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Show dropdown when value ends with @
  useEffect(() => {
    if (value && value.endsWith('@')) {
      setShowMentionList(true);
    }
  }, [value]);

  const fetchMentionableUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/mentionable`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMentionableUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch mentionable users:", error);
    }
  };

  const handleKeyDown = (e) => {
    // Close on Escape
    if (e.key === 'Escape') {
      setShowMentionList(false);
    }
  };

  const handleSelectUser = (user) => {
    // Find the last @ in the current value and replace from there
    const lastAtIndex = value.lastIndexOf('@');
    
    if (lastAtIndex >= 0) {
      const textBeforeAt = value.substring(0, lastAtIndex);
      const newValue = `${textBeforeAt}@${user.name} `;
      onChange(newValue);
    }
    
    setShowMentionList(false);
    
    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 10);
  };

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        className={`flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      />
      
      {/* Mention hint */}
      {mentionableUsers.length > 0 && (
        <div className="absolute right-2 top-2 text-slate-400">
          <AtSign className="w-4 h-4" title="Type @ to mention someone" />
        </div>
      )}

      {/* Mention List Dropdown */}
      {showMentionList && mentionableUsers.length > 0 && (
        <div 
          ref={mentionListRef}
          className="absolute z-[100] mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
          style={{ zIndex: 9999 }}
        >
          <div className="px-3 py-2 bg-slate-50 border-b text-xs font-medium text-slate-500">
            Select a person to mention
          </div>
          <div className="max-h-48 overflow-y-auto">
            {mentionableUsers.map((user) => (
              <div
                key={user.user_id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectUser(user);
                }}
                className="w-full text-left px-3 py-2 hover:bg-indigo-50 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{user.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user.role.replace('_', ' ')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MentionSelector;
